import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TechnologyStack = () => {
  const [activeCategory, setActiveCategory] = useState('frontend');

  const categories = [
    { id: 'frontend', label: 'Frontend', icon: 'Layout' },
    { id: 'backend', label: 'Backend', icon: 'Server' },
    { id: 'mobile', label: 'Mobile', icon: 'Smartphone' },
    { id: 'cloud', label: 'Cloud & DevOps', icon: 'Cloud' },
    { id: 'database', label: 'Database', icon: 'Database' }
  ];

  const technologies = {
    frontend: [
      { name: 'React', description: 'Modern UI development with component-based architecture', icon: 'Code2' },
      { name: 'Next.js', description: 'Server-side rendering and static site generation', icon: 'Zap' },
      { name: 'TypeScript', description: 'Type-safe JavaScript for scalable applications', icon: 'FileCode' },
      { name: 'TailwindCSS', description: 'Utility-first CSS framework for rapid UI development', icon: 'Palette' },
      { name: 'Vue.js', description: 'Progressive framework for building user interfaces', icon: 'Layers' },
      { name: 'Angular', description: 'Enterprise-grade framework for complex applications', icon: 'Box' }
    ],
    backend: [
      { name: 'Node.js', description: 'JavaScript runtime for scalable server applications', icon: 'Server' },
      { name: 'Python/Django', description: 'Rapid development with clean, pragmatic design', icon: 'Code' },
      { name: 'Java/Spring', description: 'Enterprise Java framework for robust applications', icon: 'Coffee' },
      { name: 'Go', description: 'High-performance concurrent programming language', icon: 'Zap' },
      { name: 'Ruby on Rails', description: 'Convention over configuration web framework', icon: 'Gem' },
      { name: 'GraphQL', description: 'Efficient API query language and runtime', icon: 'Network' }
    ],
    mobile: [
      { name: 'React Native', description: 'Cross-platform mobile development with React', icon: 'Smartphone' },
      { name: 'Flutter', description: 'Beautiful native apps from single codebase', icon: 'Layers' },
      { name: 'Swift/iOS', description: 'Native iOS development with modern Swift', icon: 'Apple' },
      { name: 'Kotlin/Android', description: 'Modern Android development with Kotlin', icon: 'Smartphone' },
      { name: 'Ionic', description: 'Hybrid mobile apps with web technologies', icon: 'Zap' },
      { name: 'PWA', description: 'Progressive web apps for mobile experiences', icon: 'Globe' }
    ],
    cloud: [
      { name: 'AWS', description: 'Comprehensive cloud computing platform', icon: 'Cloud' },
      { name: 'Google Cloud', description: 'Scalable infrastructure and machine learning', icon: 'CloudCog' },
      { name: 'Azure', description: 'Microsoft cloud platform for enterprise solutions', icon: 'CloudLightning' },
      { name: 'Docker', description: 'Containerization for consistent deployments', icon: 'Package' },
      { name: 'Kubernetes', description: 'Container orchestration at scale', icon: 'Boxes' },
      { name: 'CI/CD', description: 'Automated testing and deployment pipelines', icon: 'GitBranch' }
    ],
    database: [
      { name: 'PostgreSQL', description: 'Advanced open-source relational database', icon: 'Database' },
      { name: 'MongoDB', description: 'Flexible NoSQL document database', icon: 'FileJson' },
      { name: 'Redis', description: 'In-memory data structure store for caching', icon: 'Zap' },
      { name: 'MySQL', description: 'Reliable relational database management system', icon: 'Database' },
      { name: 'Elasticsearch', description: 'Distributed search and analytics engine', icon: 'Search' },
      { name: 'Firebase', description: 'Real-time database and backend services', icon: 'Flame' }
    ]
  };

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4 md:mb-6">
            <Icon name="Cpu" size={18} color="var(--color-primary)" />
            <span className="text-xs md:text-sm font-medium font-cta text-primary">
              Technology Expertise
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-foreground mb-4 md:mb-6">
            Our Technology Stack
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            We leverage cutting-edge technologies and proven frameworks to build scalable, maintainable, and high-performance SaaS applications tailored to your business needs.
          </p>
        </div>

        <div className="mb-8 md:mb-12 overflow-x-auto">
          <div className="flex items-center justify-center space-x-2 md:space-x-4 min-w-max px-4 md:px-0">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setActiveCategory(category?.id)}
                className={`flex items-center space-x-2 px-4 md:px-6 py-2 md:py-3 rounded-md font-medium font-cta text-sm md:text-base transition-all duration-200 whitespace-nowrap ${
                  activeCategory === category?.id
                    ? 'bg-primary text-primary-foreground shadow-lg'
                    : 'bg-card text-foreground hover:bg-muted border border-border'
                }`}
              >
                <Icon name={category?.icon} size={18} />
                <span>{category?.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {technologies?.[activeCategory]?.map((tech, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-lg hover:border-primary/50 transition-all duration-300 group"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors duration-200">
                  <Icon name={tech?.icon} size={24} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg md:text-xl font-semibold font-headline text-foreground mb-2 group-hover:text-primary transition-colors duration-200">
                    {tech?.name}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground font-body leading-relaxed">
                    {tech?.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 text-center">
          <p className="text-sm md:text-base text-muted-foreground font-body mb-6">
            Don't see your preferred technology? We're always expanding our expertise.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-6 md:px-8 py-3 md:py-4 rounded-md font-medium font-cta text-sm md:text-base hover:bg-primary/90 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <span>Discuss Your Tech Stack</span>
            <Icon name="MessageSquare" size={18} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default TechnologyStack;